import { useEffect, useRef, useState, type SubmitEvent } from "react";
import { useSession, signOut } from "@/frontend/auth-client";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/frontend/components/ui/avatar";
import { Separator } from "@/frontend/components/ui/separator";
import { Loader2, Camera, ChevronRight, XCircle } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/frontend/components/ui/field";
import { getInitials, readFileAsBase64 } from "@/frontend/utils/shared";
import {
  useMyMemberProfileQuery,
} from "@/frontend/queries/dashboard/settings";
import {
  useUpdateUserMutation,
  useUploadMemberImageMutation,
  useDeleteOwnAccountMutation,
} from "@/frontend/mutations/dashboard/settings";

/**
 * Settings page for the signed-in user's avatar, display name and account deletion.
 * Deleting the account asks the user to type their email first, then signs out.
 * @returns The profile settings, or nothing while there is no session.
 */
export function ProfileSection() {
  const { data: session } = useSession();
  const memberProfileQuery = useMyMemberProfileQuery(!!session?.user);
  const currentDisplayName = session?.user?.name ?? "";
  const [name, setName] = useState(currentDisplayName);
  const [isEditing, setIsEditing] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const memberImage = memberProfileQuery.data?.image;
  const displayImage = memberImage ?? session?.user?.image;

  const updateMutation = useUpdateUserMutation(() => setIsEditing(false));
  const uploadMemberImageMutation = useUploadMemberImageMutation();
  const deleteOwnAccountMutation = useDeleteOwnAccountMutation();

  useEffect(() => {
    if (!isEditing) {
      setName(currentDisplayName);
    }
  }, [currentDisplayName, isEditing]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setAvatarError(null);
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError("Image must be 2 MB or smaller");
      return;
    }
    try {
      const base64 = await readFileAsBase64(file);
      uploadMemberImageMutation.mutate(
        { imageBase64: base64 },
        { onError: () => setAvatarError(null) }
      );
    } catch {
      setAvatarError("Failed to read file");
    }
    e.target.value = "";
  };

  if (!session?.user) return null;
  const user = session.user;
  const initials = getInitials(currentDisplayName, user.email);
  const canUploadAvatar = !!memberProfileQuery.data; // in org with member record
  const saveName = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    updateMutation.mutate(trimmedName);
  };
  const nameError = updateMutation.error?.message;
  const isSavingName = updateMutation.isPending;
  const isDeletingAccount = deleteOwnAccountMutation.isPending || deleteOwnAccountMutation.isSuccess;
  const confirmEmailMatches = deleteConfirmEmail.trim().toLowerCase() === user.email.trim().toLowerCase();

  /**
   * Opens or closes the delete-account confirmation, ignoring a close while the delete runs.
   * Closing clears the typed email and any earlier error.
   * @param nextOpen Whether the confirmation should now be open.
   */
  const handleDeleteAccountDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isDeletingAccount) return;
    setShowDeleteAccountDialog(nextOpen);
    if (!nextOpen) {
      setDeleteConfirmEmail("");
      deleteOwnAccountMutation.reset();
    }
  };

  /**
   * Deletes the account once the typed email matches, then signs out and reloads the page.
   * The server checks the same email again before it deletes anything.
   * @param event The confirmation form's submit event.
   */
  const handleDeleteAccountSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!confirmEmailMatches || isDeletingAccount) return;
    deleteOwnAccountMutation.mutate({ email: deleteConfirmEmail }, {
      onSuccess: () => {
        signOut().then(() => window.location.reload());
      },
    });
  };

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-xl font-semibold">My Profile</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your personal information and how others see you.
        </p>
      </div>

      <Separator />

      {/* Avatar row — org-specific profile pic */}
      <div className="flex items-center gap-6">
        <div className="relative shrink-0">
          <Avatar className="size-20">
            <AvatarImage src={displayImage || undefined} alt={user.name || user.email} />
            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
          </Avatar>
          {canUploadAvatar && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMemberImageMutation.isPending}
              className="absolute bottom-0 right-0 flex size-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {uploadMemberImageMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Camera className="size-3.5" />
              )}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handleAvatarUpload}
          />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">
            {canUploadAvatar
              ? "Click the camera icon to upload a profile picture."
              : "Select a workspace to upload a profile picture."}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">JPG, PNG, GIF or WebP · max 2 MB</p>
          {(avatarError || uploadMemberImageMutation.isError) && (
            <p className="text-sm text-destructive mt-1">
              {avatarError ?? uploadMemberImageMutation.error?.message}
            </p>
          )}
        </div>
      </div>

      <Separator />

      {/* Name row */}
      <div className="flex flex-col gap-4 max-w-md">
        <div>
          <p className="text-sm font-medium mb-1">Display Name</p>
          <p className="text-sm text-muted-foreground">
            Your display name across all workspaces.
          </p>
        </div>
        {isEditing ? (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="display-name" className="sr-only">Display Name</FieldLabel>
              <Input
                id="display-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoFocus
              />
            </Field>
            {nameError && (
              <p className="text-sm text-destructive">{nameError}</p>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={saveName}
                disabled={isSavingName || !name.trim()}
              >
                {isSavingName ? (
                  <><Loader2 className="animate-spin" data-icon="inline-start" />Saving…</>
                ) : "Save changes"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setName(currentDisplayName); setIsEditing(false); }}
              >
                Cancel
              </Button>
            </div>
          </FieldGroup>
        ) : (
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div>
              <p className="font-medium">{currentDisplayName || "No name set"}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
              Edit <ChevronRight className="size-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* Delete my account — danger zone */}
      <Separator />
      <div className="border border-destructive/30 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <XCircle className="h-4 w-4 text-destructive shrink-0" />
          <span className="text-sm font-medium text-destructive">Delete my account</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setShowDeleteAccountDialog(true)}
        >
          Delete my account
        </Button>
      </div>

      <Dialog open={showDeleteAccountDialog} onOpenChange={handleDeleteAccountDialogOpenChange}>
        <DialogContent showCloseButton={!isDeletingAccount}>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This deletes your account, its memberships in every workspace and the API keys it created.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDeleteAccountSubmit} className="flex flex-col gap-4">
            <Field>
              <FieldLabel htmlFor="delete-account-email">
                <span>Type <span data-ph-mask className="font-semibold break-all">{user.email}</span> to confirm</span>
              </FieldLabel>
              <Input
                id="delete-account-email"
                type="email"
                autoComplete="off"
                value={deleteConfirmEmail}
                onChange={(event) => setDeleteConfirmEmail(event.target.value)}
                disabled={isDeletingAccount}
              />
            </Field>
            {deleteOwnAccountMutation.isError && (
              <p className="text-sm text-destructive">{deleteOwnAccountMutation.error.message}</p>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isDeletingAccount}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" variant="destructive" disabled={!confirmEmailMatches || isDeletingAccount}>
                {deleteOwnAccountMutation.isPending ? (
                  <><Loader2 className="size-4 animate-spin mr-2" />Deleting…</>
                ) : (
                  "Delete my account"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
